/**
 * Cloudflare Turnstile integration with modal UX.
 *
 * - Widget lives in a hidden container. On `getToken()`:
 *    - `execute()` is called.
 *    - If CF resolves quickly (< 600 ms), token returns silently — user sees nothing.
 *    - If CF requires interaction, a centered modal fades in containing the widget.
 *    - When token arrives (or user dismisses), the modal closes.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { publicApi } from '../context/AuthContext';

const SDK_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const TurnstileContext = createContext({
  ready: false,
  enabled: false,
  siteKey: '',
  getToken: async () => '',
});

function loadScript() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('no-window'));
    if (window.turnstile) return resolve();
    const existing = document.querySelector(`script[src^="${SDK_URL.split('?')[0]}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = SDK_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export function TurnstileProvider({ children }) {
  const [config, setConfig] = useState({ site_key: '', enabled: false });
  const [ready, setReady] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const widgetIdRef = useRef(null);
  const containerRef = useRef(null);
  const pendingRef = useRef([]);
  const modalTimerRef = useRef(null);

  // Fetch config once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await publicApi.get('/settings/turnstile-config');
        if (!cancelled) setConfig(data || { site_key: '', enabled: false });
      } catch {
        if (!cancelled) setConfig({ site_key: '', enabled: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load SDK + render widget once site_key is known
  useEffect(() => {
    if (!config.enabled || !config.site_key) {
      setReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await loadScript();
        if (cancelled || !window.turnstile || !containerRef.current) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: config.site_key,
          appearance: 'interaction-only',
          execution: 'execute',
          callback: (token) => {
            if (modalTimerRef.current) { clearTimeout(modalTimerRef.current); modalTimerRef.current = null; }
            setModalOpen(false);
            const queue = pendingRef.current;
            pendingRef.current = [];
            queue.forEach(({ resolve }) => resolve(token));
            try { window.turnstile.reset(widgetIdRef.current); } catch { /* noop */ }
          },
          'error-callback': () => {
            if (modalTimerRef.current) { clearTimeout(modalTimerRef.current); modalTimerRef.current = null; }
            setModalOpen(false);
            const queue = pendingRef.current;
            pendingRef.current = [];
            queue.forEach(({ resolve }) => resolve(''));
            try { window.turnstile.reset(widgetIdRef.current); } catch { /* noop */ }
          },
          'expired-callback': () => {
            try { window.turnstile.reset(widgetIdRef.current); } catch { /* noop */ }
          },
        });
        setReady(true);
      } catch {
        setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [config.enabled, config.site_key]);

  const getToken = useCallback(() => {
    if (!config.enabled || !config.site_key) return Promise.resolve('');
    return new Promise((resolve) => {
      if (!window.turnstile || widgetIdRef.current == null) {
        resolve('');
        return;
      }
      pendingRef.current.push({ resolve });
      try {
        window.turnstile.execute(widgetIdRef.current);
      } catch {
        resolve('');
        return;
      }
      // Show modal only if CF hasn't resolved within 600 ms (i.e. user interaction needed)
      if (modalTimerRef.current) clearTimeout(modalTimerRef.current);
      modalTimerRef.current = setTimeout(() => {
        if (pendingRef.current.length) setModalOpen(true);
      }, 600);
      // Safety timeout (90s) — user abandoned the challenge
      setTimeout(() => {
        if (pendingRef.current.length) {
          const queue = pendingRef.current;
          pendingRef.current = [];
          setModalOpen(false);
          queue.forEach(({ resolve: r }) => r(''));
        }
      }, 90000);
    });
  }, [config.enabled, config.site_key]);

  const dismiss = () => {
    if (modalTimerRef.current) { clearTimeout(modalTimerRef.current); modalTimerRef.current = null; }
    setModalOpen(false);
    const queue = pendingRef.current;
    pendingRef.current = [];
    queue.forEach(({ resolve }) => resolve(''));
    try { window.turnstile && window.turnstile.reset(widgetIdRef.current); } catch { /* noop */ }
  };

  return (
    <TurnstileContext.Provider value={{ ready, enabled: config.enabled, siteKey: config.site_key, getToken }}>
      {children}

      {/* Modal overlay — visible only when CF requires interaction */}
      <div
        aria-hidden={!modalOpen}
        data-testid="turnstile-modal"
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(15, 23, 42, 0.55)',
          backdropFilter: 'blur(3px)',
          zIndex: 99999,
          opacity: modalOpen ? 1 : 0,
          pointerEvents: modalOpen ? 'auto' : 'none',
          transition: 'opacity 180ms ease-out',
        }}
        onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: 20,
            padding: '28px 28px 20px',
            width: 'min(92vw, 360px)',
            boxShadow: '0 24px 64px rgba(15, 23, 42, 0.35)',
            transform: modalOpen ? 'scale(1)' : 'scale(0.94)',
            transition: 'transform 180ms ease-out',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 12,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
            Vérification de sécurité
          </div>
          <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.4 }}>
            Merci de confirmer que vous n'êtes pas un robot pour continuer.
          </div>
          {/* Turnstile widget mounts here (container is always rendered so CF can mount on it) */}
          <div ref={containerRef} style={{ marginTop: 6 }} />
          <button
            type="button"
            onClick={dismiss}
            style={{
              marginTop: 4,
              fontSize: 12,
              color: '#94a3b8',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '6px 12px',
            }}
          >
            Annuler
          </button>
        </div>
      </div>
    </TurnstileContext.Provider>
  );
}

export function useTurnstile() {
  return useContext(TurnstileContext);
}

export async function withTurnstile(getToken, axiosCallWithHeaders) {
  const token = await getToken();
  const headers = token ? { 'X-Turnstile-Token': token } : {};
  return axiosCallWithHeaders(headers);
}
